import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { clsx } from 'clsx'

// const options = [
//    { value: 'javascript', label: 'JavaScript' },
//    { value: 'python', label: 'Python' },
//    { value: 'react', label: 'React' },
//    { value: 'nodejs', label: 'Node.js' },
//    { value: 'typescript', label: 'TypeScript' },
//    { value: 'vuejs', label: 'Vue.js' },
//    { value: 'angular', label: 'Angular' },
//    { value: 'svelte', label: 'Svelte' },
//  ]

export default function Select({
  multiple = false,
  options,
  delimiter = ',',
  label = 'select your item',
  onChange = (items) => {},
  onClose = (items) => {},
  defaultSelectedItems = [],
  defaultSelectedItem,
  selectAll = false,
  selectAllOptionLabel = 'select all',
  selectAllDisplay = 'all',
  disabled = false,
  atLeastOne = false,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState(() => {
    if (multiple) {
      return defaultSelectedItems.length > 0 ? options.filter((item) => defaultSelectedItems.includes(item.value)) : []
    } else {
      return [options.find((item) => item.value === defaultSelectedItem)]
    }
  })

  const [focusedIndex, setFocusedIndex] = useState(0)
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const optionRefs = useRef([])

  useEffect(() => {
    if (selectAll && isOpen) {
      setFocusedIndex(multiple ? -1 : 0)
    }
  }, [selectAll, isOpen, multiple])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        const values = multiple ? selectedItems.map((item) => item.value) : selectedItems[0]?.value
        onChange?.(values)
        onClose?.(values)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isOpen, selectedItems])

  useEffect(() => {
    if (selectedItems.length > 0) {
      // Find the first option in the list that is selected
      const firstSelectedIndex = options.findIndex((option) =>
        selectedItems.some((item) => item.value === option.value)
      )
      setFocusedIndex(firstSelectedIndex >= 0 ? firstSelectedIndex : 0)
    } else {
      setFocusedIndex(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [focusedIndex, isOpen])

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex((prev) => (prev + 1) % options.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex((prev) => (prev - 1 + options.length) % options.length)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        toggleOption(options[focusedIndex])
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
        break
      case 'Tab':
        setIsOpen(false)
        break
      default:
        break
    }
  }

  const toggleOption = (option) => {
    if (multiple) {
      let newSelectedItems
      if (selectedItems.some((item) => item.value === option.value)) {
        newSelectedItems = selectedItems.filter((item) => item.value !== option.value)
      } else {
        newSelectedItems = [...selectedItems, option]
      }
      // Sort by options order
      newSelectedItems.sort((a, b) => {
        const indexA = options.findIndex((opt) => opt.value === a.value)
        const indexB = options.findIndex((opt) => opt.value === b.value)
        return indexA - indexB
      })
      setSelectedItems(newSelectedItems)
      onChange?.(newSelectedItems.map((item) => item.value))
      buttonRef.current?.focus()
    } else {
      if (selectedItems.length > 0 && selectedItems[0].value === option.value && !atLeastOne) {
        setSelectedItems([])
        onChange?.([])
      } else {
        setSelectedItems([option])
        setIsOpen(false)
        onChange?.(option.value)
        onClose?.(option.value)
      }
    }
  }

  const isSelected = (value) => {
    return selectedItems.some((item) => item.value === value)
  }

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return label || 'Select options...'
    }
    if (multiple && selectAll && selectedItems.length === options.length) {
      return selectAllDisplay
    }
    return selectedItems.map((item) => item.label).join(`${delimiter.trim()} `)
  }

  const handleSelectAll = () => {
    if (selectedItems.length === options.length) {
      setSelectedItems([])
    } else {
      setSelectedItems([...options])
    }
  }

  return (
    <>
      <style>{`
        .util-container {
          box-sizing: border-box
          min-height: 100vh;
          background: linear-gradient(to bottom right, #eff6ff, #e0e7ff);
          padding: 2rem;
        }

        .util-content {
          max-width: 48rem;
          margin: 0 auto;
        }

        .util-title {
          font-size: 1.875rem;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 2rem;
          text-align: center;
        }

        .util-card {
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .util-label {
          display: block;
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 1rem;
        }

        .util-instructions {
          font-size: 0.875rem;
          color: #4b5563;
          margin-bottom: 0.75rem;
        }

        .util-dropdown-wrapper {
          position: relative;
        }

        .util-dropdown-button {
          min-height: 32px;
          width: 100%;
          padding: 6px 12px;
          text-align: left;
          background: white;
          border: 1px solid var(--lt-grey, #B7B7B7);
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 1rem;
        }

        .util-dropdown-button:hover {
          border-color: var(--darkest-grey, #4A4A4A);
        }

        .util-dropdown-button:focus {
          outline: none;
          border-color: var(--darkest-grey, #4A4A4A);
        }
        
        .util-dropdown-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          border-color: #d1d5db;
        }

        .util-dropdown-button:disabled:hover {
          border-color: #d1d5db;
        }

        .util-dropdown-text {
          color: #374151;
          flex: 1;
          margin-right: 0.5rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .util-dropdown-arrow {
          width: 1.25rem;
          height: 1.25rem;
          color: #6b7280;
          transition: transform 0.2s;
          flex-shrink: 0;
        }

        .util-dropdown-arrow.util-open {
          transform: rotate(180deg);
        }

        .util-dropdown-menu {
          position: absolute;
          z-index: 10;
          width: 100%;
          margin-top: 0.5rem;
          background: white;
          border: 2px solid #d1d5db;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          max-height: 15rem;
          overflow: auto;
        }

        .util-dropdown-option {
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .util-dropdown-option:hover {
          background-color: #f3f4f6;
        }

        .util-dropdown-option.util-selected {
          background-color: #eef2ff;
        }

        .util-dropdown-option.util-selected:hover {
          background-color: #e0e7ff;
        }

        .util-dropdown-option.util-focused {
          background-color: #c7d2fe;
        }

        .util-checkbox {
          width: 1.25rem;
          height: 1.25rem;
          border: 2px solid #d1d5db;
          border-radius: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .util-checkbox.util-checked {
          background-color: #6366f1;
          border-color: #6366f1;
        }

        .util-checkmark-only {
          width: 1.25rem;
          height: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .util-checkmark {
          width: 1rem;
          height: 1rem;
          color: white;
        }

        .util-checkmark-icon {
          width: 1.25rem;
          height: 1.25rem;
          color: #6366f1;
        }

        .util-option-label {
          font-weight: 500;
          color: #374151;
        }

        .util-option-label.util-selected {
          color: #4338ca;
        }

        .util-results-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 1rem;
        }

        .util-empty-state {
          color: #9ca3af;
          font-style: italic;
          text-align: center;
          padding: 2rem 0;
        }

        .util-tags-container {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .util-tag {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background-color: #e0e7ff;
          color: #3730a3;
          padding: 0.5rem 1rem;
          border-radius: 9999px;
          font-weight: 500;
        }

        .util-tag-remove {
          margin-left: 0.25rem;
          color: #4f46e5;
          font-weight: bold;
          font-size: 1.125rem;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .util-tag-remove:hover {
          color: #312e81;
        }
      `}</style>

      <div className="util-dropdown-wrapper" ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={disabled ? undefined : handleKeyDown}
          className="util-dropdown-button"
          disabled={disabled}
          type="button"
        >
          <span className="util-dropdown-text">{getDisplayText()}</span>
          <svg
            className={`util-dropdown-arrow ${isOpen ? 'util-open' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="util-dropdown-menu">
            {selectAll && multiple && (
              <div
                onClick={handleSelectAll}
                onMouseEnter={() => setFocusedIndex(-1)}
                className={clsx(
                  'util-dropdown-option',
                  selectedItems.length === options.length && 'util-selected',
                  focusedIndex === -1 && 'util-focused'
                )}
              >
                <div className={clsx('util-checkbox', selectedItems.length === options.length && 'util-checked')}>
                  {selectedItems.length === options.length && (
                    <svg className="util-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className={clsx('util-option-label', selectedItems.length === options.length && 'util-selected')}>
                  {selectAllOptionLabel}
                </span>
              </div>
            )}
            {options.map((option, index) => (
              <div
                key={option.value}
                ref={(el) => (optionRefs.current[index] = el)}
                onClick={() => toggleOption(option)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={clsx(
                  'util-dropdown-option',
                  isSelected(option.value) && 'util-selected',
                  focusedIndex === index && 'util-focused'
                )}
              >
                {multiple ? (
                  <div className={clsx('util-checkbox', isSelected(option.value) && 'util-checked')}>
                    {isSelected(option.value) && (
                      <svg className="util-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div className="util-checkmark-only">
                    {isSelected(option.value) && (
                      <svg className="util-checkmark-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                )}
                <span className={clsx('util-option-label', isSelected(option.value) && 'util-selected')}>
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

Select.propTypes = {
  multiple: PropTypes.bool,
  label: PropTypes.string,
  delimiter: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  defaultSelectedItems: PropTypes.array,
  defaultSelectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
}
